import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import TicketsManager from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import { Button, CircularProgress } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import logo from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";

const defaultTicketsManagerWidth = 380;
const minTicketsManagerWidth = 300;
const maxTicketsManagerWidth = 500;

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: "2px",
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
	},
	chatPapper: {
		display: "flex",
		height: "100%",
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		position: "relative",
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
	},
	welcomeMsg: {
		background: theme.palette.tabHeaderBackground,
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
	},
	dragger: {
		width: "5px",
		cursor: "ew-resize",
		padding: "4px 0 0",
		borderTop: "1px solid #ddd",
		position: "absolute",
		top: 0,
		right: 0,
		bottom: 0,
		zIndex: 100,
		backgroundColor: "#f4f7f9",
		userSelect: "none", // Evita a seleção de texto no elemento de redimensionamento
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")"
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);
	const { whatsApps, loading: whatsAppsLoading } = useContext(WhatsAppsContext);
	const history = useHistory();

	const classes = useStyles({ ticketsManagerWidth: user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth });

	const { ticketId } = useParams();

	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(defaultTicketsManagerWidth);
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);
	const [whatsappApiLoading, setWhatsappApiLoading] = useState(true);
	const [hasWhatsappApiConnections, setHasWhatsappApiConnections] = useState(false);
	const [socialApiLoading, setSocialApiLoading] = useState(true);
	const [hasSocialApiConnections, setHasSocialApiConnections] = useState(false);

	useEffect(() => {
		const desired = user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth;
		setTicketsManagerWidth(desired);
		ticketsManagerWidthRef.current = desired;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.defaultTicketsManagerWidth]);

	useEffect(() => {
		let mounted = true;
		const fetchCloudApiConnections = async () => {
			setWhatsappApiLoading(true);
			try {
				const { data } = await api.get("/whatsapp-api");
				if (mounted) {
					const active = Array.isArray(data) && data.some((c) => {
						const isActive = Boolean(c?.isActive);
						const status = String(c?.status || "").toLowerCase();
						return isActive && (status === "active" || status === "connected");
					});
					setHasWhatsappApiConnections(active);
				}
			} catch (_) {
				if (mounted) {
					setHasWhatsappApiConnections(false);
				}
			} finally {
				if (mounted) {
					setWhatsappApiLoading(false);
				}
			}
		};
		fetchCloudApiConnections();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		let mounted = true;
		const fetchSocialConnections = async () => {
			setSocialApiLoading(true);
			try {
				const { data } = await api.get("/channel-connections", {
					params: { channels: JSON.stringify(["facebook", "instagram"]) },
				});
				if (mounted) {
					const active = Array.isArray(data) && data.some((c) => {
						const isActive = Boolean(c?.isActive);
						const status = String(c?.status || "").toLowerCase();
						return isActive && (status === "connected" || status === "active");
					});
					setHasSocialApiConnections(active);
				}
			} catch (_) {
				if (mounted) {
					setHasSocialApiConnections(false);
				}
			} finally {
				if (mounted) {
					setSocialApiLoading(false);
				}
			}
		};
		fetchSocialConnections();
		return () => {
			mounted = false;
		};
	}, []);

	// useEffect(() => {
	// 	if (ticketId && currentTicket.uuid === undefined) {
	// 		history.push("/tickets");
	// 	}
	// }, [ticketId, currentTicket.uuid, history]);

	const handleMouseDown = (e) => {
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};
	const handleSaveContact = async value => {
		if (value < 404)
			value = 404
		await api.put(`/users/toggleChangeWidht/${user.id}`, { defaultTicketsManagerWidth: value });

	}
	const handleMouseMove = useCallback(
		(e) => {
			const newWidth = e.clientX - document.body.offsetLeft;
			if (
				newWidth > minTicketsManagerWidth &&
				newWidth < maxTicketsManagerWidth
			) {
				ticketsManagerWidthRef.current = newWidth;
				setTicketsManagerWidth(newWidth);
			}
		},
		[]
	);

	const handleMouseUp = async () => {
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;

		if (newWidth !== ticketsManagerWidth) {
			await handleSaveContact(newWidth);
		}
	};

	const hasQrWhatsAppConnections =
		Array.isArray(whatsApps) &&
		whatsApps.some((w) => String(w?.status || "").toLowerCase() === "connected");
	const hasAnyConnections = hasQrWhatsAppConnections || hasWhatsappApiConnections || hasSocialApiConnections;

	return (
		<QueueSelectedProvider>
			<div className={classes.chatContainer}>
				<div className={classes.chatPapper}>
					<div
						className={classes.contactsWrapper}
						style={{ width: ticketsManagerWidth }}
					>
						<TicketsManager />
						<div onMouseDown={e => handleMouseDown(e)} className={classes.dragger} />
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<>
								{/* <Suspense fallback={<CircularProgress />}> */}
								<Ticket />
								{/* </Suspense> */}
							</>
						) : (
							<Paper square variant="outlined" className={classes.welcomeMsg}>
								<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
									<div style={{ width: 80, height: 80, borderRadius: 20, background: 'linear-gradient(135deg, #1e3a5f, #00c9a7)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(30,58,95,0.2)' }}>
										<span style={{ fontSize: 36, filter: 'brightness(10)' }}>💬</span>
									</div>
									<span style={{ fontSize: 16, fontWeight: 600, color: '#1e3a5f' }}>
										{i18n.t("chat.noTicketMessage")}
									</span>
									{!whatsAppsLoading && !whatsappApiLoading && !socialApiLoading && !hasAnyConnections && (
										<Button
											variant="contained"
											style={{ borderRadius: 8, textTransform: 'none', background: 'linear-gradient(135deg, #1e3a5f, #00c9a7)', fontWeight: 600 }}
											onClick={() => history.push("/config-apis")}
										>
											{i18n.t("chat.configureApis")}
										</Button>
									)}
								</div>
							</Paper>
						)}
					</div>
				</div>
			</div>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;
